import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTEUDO_CHAVES, getConteudoChave } from '@/api/adminApi'
import { getPacotes } from '@/api/adminApi'
import type { Pacote } from '@/api/adminApi'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import { createCompra } from '@/api/usuariosApi'
import styles from './PacotesPage.module.css'

export function PacotesPage() {
  const navigate = useNavigate()
  const { user } = useUser()
  const { toast } = useToast()
  const [pacotes, setPacotes] = useState<Pacote[]>([])
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)
  const [pacoteId, setPacoteId] = useState<number | null>(null)
  const [concordo, setConcordo] = useState(false)
  const [metodoPagamento, setMetodoPagamento] = useState<'fisico' | 'mbway' | 'transferencia'>('fisico')
  const [pacotesEnabled, setPacotesEnabled] = useState(true)
  const [loadingFeature, setLoadingFeature] = useState(true)

  useEffect(() => {
    let cancelled = false
    getConteudoChave(CONTEUDO_CHAVES.pacotes_enabled)
      .then((value) => {
        if (!cancelled) setPacotesEnabled((value ?? '1') !== '0')
      })
      .catch(() => {
        if (!cancelled) setPacotesEnabled(true)
      })
      .finally(() => {
        if (!cancelled) setLoadingFeature(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (loadingFeature || !pacotesEnabled) return
    let cancelled = false
    getPacotes(false)
      .then((list) => { if (!cancelled) setPacotes(list) })
      .catch(() => { if (!cancelled) setPacotes([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [loadingFeature, pacotesEnabled])

  useEffect(() => {
    if (!loadingFeature && !pacotesEnabled) navigate('/menu', { replace: true })
  }, [loadingFeature, pacotesEnabled, navigate])

  const pacote = pacoteId ? pacotes.find((p) => p.id === pacoteId) : null

  const handleConfirmarStep1 = () => {
    if (pacoteId) setStep(2)
  }

  const handleConfirmarStep2 = () => {
    if (concordo) setStep(3)
  }

  const handleFinalizarCompra = async () => {
    if (!user?.id || !pacoteId) return
    const metodo = metodoPagamento === 'fisico'
      ? 'fisico_local'
      : metodoPagamento === 'mbway'
        ? 'mbway'
        : 'transferencia'

    try {
      await createCompra({
        pacote_id: pacoteId,
        metodo_pagamento: metodo,
        status: metodo === 'fisico_local' ? 'pago_local' : 'pendente',
      })
      toast('Compra registada com sucesso.', 'success')
      navigate('/historico')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao registar compra.', 'error')
    }
  }

  return (
    <main className={`${styles.page} animate-slide-up`}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => (step === 1 ? navigate('/menu') : setStep(step - 1))}
      >
        ← Voltar
      </button>

      <div className={styles.card}>
        <div className={styles.steps}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`${styles.stepDot} ${step === s ? styles.active : ''}`}
              aria-hidden
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <h1 className={styles.heading}>Escolha seu pacote</h1>
            {loading ? (
              <p style={{ color: 'var(--text-muted)' }}>A carregar…</p>
            ) : pacotes.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Nenhum pacote disponível.</p>
            ) : (
              <>
                <div className={styles.packageList}>
                  {pacotes.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`${styles.packageOption} ${pacoteId === p.id ? styles.selected : ''}`}
                      onClick={() => setPacoteId(p.id)}
                    >
                      {p.nome}
                      {p.preco != null && <span className={styles.preco}>€ {Number(p.preco).toFixed(2)}</span>}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.btnConfirmar}
                  disabled={!pacoteId}
                  onClick={handleConfirmarStep1}
                >
                  Confirmar
                </button>
              </>
            )}
          </>
        )}

        {step === 2 && pacote && (
          <div key="step2" className="animate-scale-in">
            <h1 className={styles.heading}>
              Informações sobre o pacote contratado
            </h1>
            <p className={styles.infoText}>{pacote.descricao || 'Sem descrição.'}</p>
            {pacote.preco != null && <p className={styles.infoText}>Preço: € {Number(pacote.preco).toFixed(2)}</p>}
            <div className={styles.agreeRow}>
              <input
                type="checkbox"
                id="concordo"
                className={styles.checkbox}
                checked={concordo}
                onChange={(e) => setConcordo(e.target.checked)}
              />
              <label htmlFor="concordo" className={styles.agreeLabel}>
                Concordo
              </label>
            </div>
            <button
              type="button"
              className={styles.btnConfirmar}
              disabled={!concordo}
              onClick={handleConfirmarStep2}
            >
              Confirmar
            </button>
          </div>
        )}

        {step === 3 && (
          <div key="step3" className="animate-scale-in">
            <h1 className={styles.heading}>Pagamento</h1>
            <p className={styles.infoText}>
              Escolha como pretende pagar o pacote:
            </p>

            <div className={styles.paymentOptions}>
              <label className={`${styles.paymentOption} ${metodoPagamento === 'fisico' ? styles.paymentOptionActive : ''}`}>
                <input
                  type="radio"
                  name="metodoPagamento"
                  value="fisico"
                  checked={metodoPagamento === 'fisico'}
                  onChange={() => setMetodoPagamento('fisico')}
                />
                <span className={styles.paymentOptionText}>Pagar físico ao local</span>
                <span className={styles.paymentOptionHint}>Pagamento no dia/levantamento</span>
              </label>

              <label className={`${styles.paymentOption} ${metodoPagamento === 'mbway' ? styles.paymentOptionActive : ''}`}>
                <input
                  type="radio"
                  name="metodoPagamento"
                  value="mbway"
                  checked={metodoPagamento === 'mbway'}
                  onChange={() => setMetodoPagamento('mbway')}
                />
                <span className={styles.paymentOptionText}>MB Way (em breve)</span>
                <span className={styles.paymentOptionHint}>Opção em desenvolvimento</span>
              </label>

              <label className={`${styles.paymentOption} ${metodoPagamento === 'transferencia' ? styles.paymentOptionActive : ''}`}>
                <input
                  type="radio"
                  name="metodoPagamento"
                  value="transferencia"
                  checked={metodoPagamento === 'transferencia'}
                  onChange={() => setMetodoPagamento('transferencia')}
                />
                <span className={styles.paymentOptionText}>Transferência bancária (em breve)</span>
                <span className={styles.paymentOptionHint}>Opção em desenvolvimento</span>
              </label>
            </div>

            <p className={styles.paymentNote}>
              Selecionado: <strong>{metodoPagamento === 'fisico' ? 'Pagar físico ao local' : metodoPagamento === 'mbway' ? 'MB Way' : 'Transferência bancária'}</strong>
            </p>
            <button
              type="button"
              className={styles.btnConfirmar}
              disabled={!user?.id || !pacoteId}
              onClick={handleFinalizarCompra}
            >
              Confirmar compra
            </button>
          </div>
        )}
      </div>
    </main>
  )
}
